import { useState } from "react";
import { Button } from "./ui/button";
import { Checkbox } from "./ui/checkbox";
import { Label } from "./ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { Settings2, GripVertical } from "lucide-react";

// Default columns configuration
// Ordre optimisé : infos essentielles + statut visible immédiatement + montants côte à côte
export const DEFAULT_COLUMNS = [
  { id: 'societe', label: 'Société', visible: true, sortable: true },
  { id: 'type', label: 'Type', visible: true },
  { id: 'etat', label: 'État', visible: true },
  { id: 'status', label: 'Statut', visible: true },
  { id: 'produit', label: 'Produit', visible: true },
  { id: 'abonnement_gi', label: 'Abo. Gestion Int.', visible: true, align: 'right' },
  { id: 'mrr', label: 'MRR', visible: true, align: 'right' },
  { id: 'vendeur', label: 'Vendeur', visible: true },
  { id: 'revendeur', label: 'Revendeur', visible: true },
  { id: 'leaser', label: 'Leaser', visible: true },
  { id: 'date_fin', label: 'Date fin', visible: true },
  { id: 'ca_leaser', label: 'CA Leaser', visible: true, align: 'right' },
  // Additional columns that can be enabled
  { id: 'date_installation', label: 'Date installation', visible: false },
  { id: 'duree_mois', label: 'Durée (mois)', visible: false },
  { id: 'commission', label: 'Commission', visible: false, align: 'right' },
  { id: 'abonnement_fin', label: 'Abo. Finance', visible: false, align: 'right' },
  // Colonnes résiliation
  { id: 'date_demande_resiliation', label: 'Date demande résiliation', visible: false },
  { id: 'date_effet_resiliation', label: 'Date effet résiliation', visible: false },
];

// Version du schéma par défaut. Incrémenter à chaque changement d'ordre/visibilité des defaults
// pour forcer une re-migration automatique de la config utilisateur.
const COLUMNS_CONFIG_VERSION = 2;
const CONFIG_STORAGE_KEY = 'contracts_columns_config';
const VERSION_STORAGE_KEY = 'contracts_columns_config_version';

// Load columns from localStorage or use defaults
export const loadColumnsConfig = () => {
  try {
    const savedVersion = parseInt(localStorage.getItem(VERSION_STORAGE_KEY) || '1', 10);
    const saved = localStorage.getItem(CONFIG_STORAGE_KEY);

    // Migration : si la version enregistrée est < version actuelle → reset aux nouveaux défauts
    if (savedVersion < COLUMNS_CONFIG_VERSION) {
      localStorage.setItem(VERSION_STORAGE_KEY, String(COLUMNS_CONFIG_VERSION));
      localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(DEFAULT_COLUMNS));
      return DEFAULT_COLUMNS;
    }

    if (saved) {
      const parsed = JSON.parse(saved);
      // Merge with defaults to handle new columns added later
      const merged = DEFAULT_COLUMNS.map(defaultCol => {
        const savedCol = parsed.find(c => c.id === defaultCol.id);
        return savedCol ? { ...defaultCol, ...savedCol } : defaultCol;
      });
      // Reorder based on saved order
      const orderedIds = parsed.map(c => c.id);
      merged.sort((a, b) => {
        const indexA = orderedIds.indexOf(a.id);
        const indexB = orderedIds.indexOf(b.id);
        if (indexA === -1) return 1;
        if (indexB === -1) return -1;
        return indexA - indexB;
      });
      return merged;
    }
  } catch (e) {
    console.error('Error loading columns config:', e);
  }
  return DEFAULT_COLUMNS;
};

// Save columns to localStorage
export const saveColumnsConfig = (columns) => {
  try {
    localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(columns));
    localStorage.setItem(VERSION_STORAGE_KEY, String(COLUMNS_CONFIG_VERSION));
  } catch (e) {
    console.error('Error saving columns config:', e);
  }
};

// Réordonne le tableau de colonnes : déplace dragIndex vers dropIndex
export const reorderColumns = (columns, dragIndex, dropIndex) => {
  if (dragIndex === null || dragIndex === dropIndex) return columns;
  const newColumns = [...columns];
  const [draggedItem] = newColumns.splice(dragIndex, 1);
  newColumns.splice(dropIndex, 0, draggedItem);
  return newColumns;
};

export default function ColumnConfigPopover({ columns, setColumns }) {
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);

  const toggleColumn = (id) => {
    const newColumns = columns.map(col => 
      col.id === id ? { ...col, visible: !col.visible } : col
    );
    setColumns(newColumns);
    saveColumnsConfig(newColumns);
  };

  const handleDragStart = (e, index) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', index.toString());
    // Add drag image styling
    e.currentTarget.style.opacity = '0.5';
  };

  const handleDragEnd = (e) => {
    e.currentTarget.style.opacity = '1';
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (index !== draggedIndex) {
      setDragOverIndex(index);
    }
  };

  const handleDrop = (e, dropIndex) => {
    e.preventDefault();
    const dragIndex = draggedIndex;
    
    if (dragIndex === null || dragIndex === dropIndex) return;

    const newColumns = [...columns];
    const [draggedItem] = newColumns.splice(dragIndex, 1);
    newColumns.splice(dropIndex, 0, draggedItem);
    
    setColumns(newColumns);
    saveColumnsConfig(newColumns);
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const resetToDefault = () => {
    setColumns([...DEFAULT_COLUMNS]);
    saveColumnsConfig(DEFAULT_COLUMNS);
  };

  const visibleCount = columns.filter(c => c.visible).length;

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2" data-testid="column-config-btn">
          <Settings2 className="w-4 h-4" />
          <span className="hidden sm:inline">Colonnes</span>
          <span className="text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded-full">{visibleCount}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0" align="end">
        <div className="p-3 border-b">
          <div className="flex items-center justify-between">
            <h4 className="font-medium text-sm">Configurer les colonnes</h4>
            <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={resetToDefault}>
              Réinitialiser
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Glissez pour réorganiser, cochez pour afficher
          </p>
        </div>
        <div className="max-h-[400px] overflow-y-auto p-2">
          {columns.map((column, index) => (
            <div
              key={column.id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={(e) => handleDrop(e, index)}
              className={`
                flex items-center gap-2 p-2 rounded-md cursor-move transition-colors
                ${dragOverIndex === index ? 'bg-blue-50 border-2 border-blue-300 border-dashed' : 'hover:bg-gray-50'}
                ${draggedIndex === index ? 'opacity-50' : ''}
              `}
              data-testid={`column-item-${column.id}`}
            >
              <GripVertical className="w-4 h-4 text-gray-400 flex-shrink-0" />
              <Checkbox
                id={`col-${column.id}`}
                checked={column.visible}
                onCheckedChange={() => toggleColumn(column.id)}
                className="flex-shrink-0"
              />
              <Label 
                htmlFor={`col-${column.id}`} 
                className="text-sm cursor-pointer flex-1 select-none"
              >
                {column.label}
              </Label>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}
